import type { ScreenPoint } from '../core/types';

export interface PinchStep {
  /** Zoom multiplier since the previous step (distance ratio). */
  readonly factor: number;
  /** Midpoint between the fingers BEFORE this step (the zoom anchor). */
  readonly from: ScreenPoint;
  /** Midpoint after this step (where the anchored world point should end up). */
  readonly to: ScreenPoint;
}

/**
 * Pure two-finger pinch / pan tracker. Engine-free so it can be unit tested.
 *
 * Each step reports the change since the previous step: how much the finger
 * distance grew (zoom factor) and how the midpoint moved (pan). Applying
 * "zoom by factor about `from`, then move `from` to `to`" keeps the world point
 * that was between the fingers between the fingers — the map sticks to them.
 */
export class PinchGesture {
  private readonly points = new Map<number, ScreenPoint>();
  private lastDist = 0;
  private lastMid: ScreenPoint = { x: 0, y: 0 };

  /** Fingers closer than this (px) give unstable ratios; distance is floored to it. */
  constructor(private readonly minDistance = 12) {}

  get active(): boolean {
    return this.points.size === 2;
  }

  get fingerCount(): number {
    return this.points.size;
  }

  has(id: number): boolean {
    return this.points.has(id);
  }

  /** Add a finger. Returns true when this finger starts a pinch (now 2 fingers). Extra fingers are ignored. */
  down(id: number, p: ScreenPoint): boolean {
    if (this.points.size >= 2 || this.points.has(id)) return false;
    this.points.set(id, { x: p.x, y: p.y });
    if (this.points.size === 2) {
      this.lastDist = this.distance();
      this.lastMid = this.midpoint();
      return true;
    }
    return false;
  }

  /** Move a finger. Returns the step to apply, or null when no pinch is active / nothing changed. */
  move(id: number, p: ScreenPoint): PinchStep | null {
    if (!this.points.has(id)) return null;
    this.points.set(id, { x: p.x, y: p.y });
    if (!this.active) return null;
    const dist = this.distance();
    const mid = this.midpoint();
    const step: PinchStep = { factor: dist / this.lastDist, from: this.lastMid, to: mid };
    this.lastDist = dist;
    this.lastMid = mid;
    if (step.factor === 1 && step.from.x === step.to.x && step.from.y === step.to.y) return null;
    return step;
  }

  /** Remove a finger. Returns true if this ended an active pinch. */
  up(id: number): boolean {
    if (!this.points.has(id)) return false;
    const wasActive = this.active;
    this.points.delete(id);
    return wasActive;
  }

  reset(): void {
    this.points.clear();
  }

  private distance(): number {
    const [a, b] = [...this.points.values()] as [ScreenPoint, ScreenPoint];
    return Math.max(this.minDistance, Math.hypot(a.x - b.x, a.y - b.y));
  }

  private midpoint(): ScreenPoint {
    const [a, b] = [...this.points.values()] as [ScreenPoint, ScreenPoint];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
}
